/**
 * Auth Service
 * Handles authentication API calls
 */

import HttpService from '@/services/http';
import StorageService from '@/services/storage';
import { AuthCredentials, AuthResponse, User } from '@/types';

const AUTH_API = '/auth';

export class AuthService {
  /**
   * Login with email and password
   */
  static async login(credentials: AuthCredentials): Promise<AuthResponse> {
    const response = await HttpService.post<AuthResponse>(
      `${AUTH_API}/login`,
      credentials
    );

    if (response.data) {
      const { access_token, refresh_token, user } = response.data;
      StorageService.setAuthData(access_token, refresh_token || null, user);
      return response.data;
    }

    throw new Error('Login failed');
  }

  /**
   * Register new user
   */
  static async register(data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Promise<AuthResponse> {
    const response = await HttpService.post<AuthResponse>(
      `${AUTH_API}/register`,
      data
    );

    if (response.data) {
      const { access_token, refresh_token, user } = response.data;
      StorageService.setAuthData(access_token, refresh_token || null, user);
      return response.data;
    }

    throw new Error('Registration failed');
  }

  /**
   * Logout current user
   */
  static async logout(): Promise<void> {
    try {
      await HttpService.post(`${AUTH_API}/logout`);
    } finally {
      StorageService.clearAuth();
    }
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<AuthResponse> {
    const refreshToken = StorageService.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await HttpService.post<AuthResponse>(
      `${AUTH_API}/refresh`,
      { refresh_token: refreshToken }
    );

    if (response.data) {
      const { access_token, refresh_token, user } = response.data;
      StorageService.setAuthData(access_token, refresh_token || null, user);
      return response.data;
    }

    throw new Error('Token refresh failed');
  }

  /**
   * Get current user
   */
  static async me(): Promise<User> {
    const response = await HttpService.get<User>(`${AUTH_API}/me`);

    if (response.data) {
      StorageService.setUser(response.data);
      return response.data;
    }

    throw new Error('Failed to fetch user');
  }

  /**
   * Request password reset
   */
  static async forgotPassword(email: string): Promise<void> {
    await HttpService.post(`${AUTH_API}/forgot-password`, { email });
  }

  /**
   * Reset password with token
   */
  static async resetPassword(data: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
  }): Promise<void> {
    await HttpService.post(`${AUTH_API}/reset-password`, data);
  }

  /**
   * Verify email
   */
  static async verifyEmail(token: string): Promise<void> {
    await HttpService.post(`${AUTH_API}/verify-email`, { token });
  }

  /**
   * Resend verification email
   */
  static async resendVerificationEmail(email: string): Promise<void> {
    await HttpService.post(`${AUTH_API}/resend-verification`, { email });
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return StorageService.isAuthenticated();
  }

  /**
   * Get current user from storage
   */
  static getCurrentUser(): User | null {
    return StorageService.getUser();
  }

  /**
   * Clear auth data
   */
  static clearAuth(): void {
    StorageService.clearAuth();
  }
}

export default AuthService;
